/**
 * Eén kaart in de lijst: kanaalicoon, naam, tijd, onderwerp en daaronder
 * het pilletje met woord. Op de gekozen en de aangewezen kaart drie
 * knopjes rechtsboven: sluiten, spam, verwijderen.
 *
 * Motor 7 golf 2, sessie D (keuze 3 van 75, mock v10): naam, kanaal, de
 * reden-pil (het overdrachtsblok van motor 7), het onderwerp en
 * "Binnen 14:02". Een belofte-tijd staat er alleen bij een echte belofte; de
 * vaste regel "antwoord voor" en de wachttijd zijn weg. Een statuspil alleen
 * als hij iets zegt wat de tegel niet al zegt (live, wacht op vervoerder,
 * gepland, versturen mislukt).
 *
 * Sessie 161: geen "Lees het hele gesprek" meer onder de kaart (JP gebruikt het
 * niet).
 *
 * Sessie 161 (bouwer E): op de iPhone lag het actiemenu over de tekst. Daar
 * staat nu een eigen knop met drie puntjes in een eigen kolom rechts (tikdoel
 * 44 px); pas na een tik verschijnen de acties, in een eigen rij onder de
 * tekst. Desktop houdt het menu bij hover (lijst.css, "Actiemenu op mobiel").
 *
 * Sessie 53: een vinkbare kaart (stand akkoord) krijgt links een
 * aanvinkvakje. Een tik op het vakje selecteert, een tik op de kaart
 * opent de zaak zoals altijd. Mislukte verwerking staat onder de kaart.
 */
import { useState } from "react";

import type { Groep, V6Kaart } from "../api";
import { Icoon } from "../Icoon";

import "../gedeeld/gedeeld.css";

import { dagTijd, KANAAL_ICOON, KANAAL_KLASSE, MOTOR7_REDEN_WOORD, tijdWoord } from "./hulp";

export type Handeling = "sluiten" | "spam" | "verwijderen";

/** Het woord op het pilletje van een live-kaart (ontwerp v6, met stip). */
export const LIVE_WOORD = "Live";

/** Statuswoorden die alleen herhalen wat de tegel al zegt (server: werkplek_kaarten). */
const STIL_WOORD = new Set(["Wacht op jou", "Beslissen", "Open"]);

/** Het laatste bericht van een live-gesprek, uit de live-stroom. */
export interface LaatsteBericht {
  regel: string;
  op: string;
}

export function Kaart({
  kaart,
  groep,
  gekozen,
  klaar = false,
  bezig = false,
  nu,
  onKies,
  onHandel,
  vinkbaar = false,
  aangevinkt = false,
  onVink,
  vinkFout = null,
  laatste = null,
  mapWoord = null,
}: {
  kaart: V6Kaart;
  /** De groep zoals het scherm hem toont (live kan de server overschrijven). */
  groep: Groep;
  liveOverschreven?: boolean;
  gekozen: boolean;
  /** Afgerond of spam: grijs, geen knopjes. */
  klaar?: boolean;
  bezig?: boolean;
  nu: Date;
  onKies: (zaakId: string) => void;
  onHandel?: (soort: Handeling, zaakId: string) => void;
  /** Toon het aanvinkvakje (alleen open kaarten met `vinkbaar`). */
  vinkbaar?: boolean;
  aangevinkt?: boolean;
  onVink?: (zaakId: string, aan: boolean) => void;
  /** De reden waarom verwerken mislukte; blijft onder de kaart staan. */
  vinkFout?: string | null;
  /** Live: het laatste bericht en zijn tijd (sessie 70, punt 6). */
  laatste?: LaatsteBericht | null;
  /** Zoekresultaat: de map waarin de zaak staat ("Open", "Afgerond"). */
  mapWoord?: string | null;
}) {
  const [menuOpen, setMenuOpen] = useState(false);
  const live = !klaar && groep === "live";
  const woord = live
    ? LIVE_WOORD
    : !klaar && kaart.stand === "akkoord"
      ? "Akkoord"
      : kaart.status_woord;
  const vink = vinkbaar && !klaar && Boolean(onVink);
  const chipKlasse = !klaar && live ? "v6-chip live" : "v6-chip";
  const toonWoord = klaar || live || !STIL_WOORD.has(woord);
  const regel = live && laatste?.regel ? laatste.regel : kaart.onderwerp;
  // Keuze 3 van 75: de binnenkomst, en een belofte-tijd alleen bij een belofte.
  // Een dichte kaart zonder binnenkomst houdt zijn tijd van het laatste bericht.
  const binnen = dagTijd(kaart.binnen, nu);
  const belofte = klaar ? "" : dagTijd(kaart.belofte_voor, nu);
  const tijd = live && laatste?.op ? tijdWoord(laatste.op, nu) : binnen ? "" : tijdWoord(kaart.moment, nu);
  const redenen = klaar
    ? []
    : (kaart.motor7_redenen ?? [])
        .map((r) => MOTOR7_REDEN_WOORD[r] ?? r.replace(/_/g, " "))
        .filter((w, i, alle) => alle.indexOf(w) === i);
  const klassen = [
    "v6-kaart",
    kaart.nieuw && !klaar ? "nieuw" : "",
    gekozen ? "on" : "",
    klaar ? "klaar" : "",
    vink ? "vinkbaar" : "",
    vink && aangevinkt ? "aangevinkt" : "",
  ]
    .filter(Boolean)
    .join(" ");

  return (
    <div className={klassen} data-zaak={kaart.zaak_id}>
      {vink ? (
        <label className="v6-vink">
          <input
            type="checkbox"
            aria-label={`${kaart.naam} aanvinken`}
            checked={aangevinkt}
            disabled={bezig}
            onChange={(e) => onVink?.(kaart.zaak_id, e.target.checked)}
          />
        </label>
      ) : null}
      <button
        type="button"
        className="v6-kaart-knop"
        aria-current={gekozen ? "true" : undefined}
        onClick={() => onKies(kaart.zaak_id)}
      >
        {kaart.kanaal === "taak" ? (
          <span className="ic taak" title="Taak">
            <Icoon naam="lijstje" />
          </span>
        ) : (
          <span className={`ic ${KANAAL_KLASSE[kaart.kanaal]}`}>
            <Icoon naam={KANAAL_ICOON[kaart.kanaal]} />
          </span>
        )}
        <span className="naam">{kaart.naam}</span>
        {tijd ? <span className="tijd num">{tijd}</span> : <span />}
        <span className="regel">{regel}</span>
        <span className="onder">
          {toonWoord ? (
            <span className={chipKlasse}>
              {live ? <span className="stip" aria-hidden="true" /> : null}
              {woord}
            </span>
          ) : null}
          {mapWoord ? <span className="map-woord">Map {mapWoord}</span> : null}
          {redenen.map((w) => (
            <span key={w} className="m61-reden-pil v6-reden" title={`Naar jou: ${w}`}>
              <Icoon naam="uitroep" klein />
              {w}
            </span>
          ))}
        </span>
        {binnen || belofte ? (
          <span className="tijden">
            {binnen ? <span data-testid="kaart-binnen">Binnen <span className="num">{binnen}</span></span> : null}
            {belofte ? (
              <span data-testid="kaart-belofte">
                {binnen ? " · " : ""}belofte vóór <b className="num">{belofte}</b>
              </span>
            ) : null}
          </span>
        ) : null}
      </button>
      {!klaar && onHandel ? (
        <button
          type="button"
          className="v6-kaart-meer"
          aria-label={`Acties voor ${kaart.naam}`}
          aria-expanded={menuOpen}
          aria-controls={`acties-${kaart.zaak_id}`}
          onClick={() => setMenuOpen((open) => !open)}
        >
          <svg viewBox="0 0 16 16" width="16" height="16" aria-hidden="true">
            <circle cx="3" cy="8" r="1.4" fill="currentColor" />
            <circle cx="8" cy="8" r="1.4" fill="currentColor" />
            <circle cx="13" cy="8" r="1.4" fill="currentColor" />
          </svg>
        </button>
      ) : null}
      {!klaar && onHandel ? (
        <span
          className={menuOpen ? "acties open" : "acties"}
          id={`acties-${kaart.zaak_id}`}
          data-testid="kaart-acties"
        >
          <button
            type="button"
            title="Sluiten"
            aria-label="Sluiten"
            disabled={bezig}
            onClick={() => {
              setMenuOpen(false);
              onHandel("sluiten", kaart.zaak_id);
            }}
          >
            <Icoon naam="sluit" klein />
          </button>
          <button
            type="button"
            title="Spam"
            aria-label="Spam"
            disabled={bezig}
            onClick={() => {
              setMenuOpen(false);
              onHandel("spam", kaart.zaak_id);
            }}
          >
            <Icoon naam="spam" klein />
          </button>
          <button
            type="button"
            title="Verwijderen"
            aria-label="Verwijderen"
            disabled={bezig}
            onClick={() => {
              setMenuOpen(false);
              onHandel("verwijderen", kaart.zaak_id);
            }}
          >
            <Icoon naam="prul" klein />
          </button>
        </span>
      ) : null}
      {vinkFout ? (
        <div className="v6-fout v6-kaart-fout" role="alert">
          {vinkFout}
        </div>
      ) : null}
    </div>
  );
}
